/* Directories — per-section, nested folders records are filed into
   (designs/directories.md DIR1/DIR8). One rule set: the server is the
   authority, the client reuses it for instant form feedback (like D8). */
import type { Stamp } from "./organizer-roles";

/* projects plus the creatable portfolio kinds; Teams/Permissions mirror
   Entra and hold no user folders (DIR10) */
export const DIRECTORY_SECTIONS = ["projects", "product", "platform"] as const;
/* retired (portfolio-mapping.md P11): no longer a section a NEW folder can
   be filed into, but an existing folder from before the kill must stay
   readable — so the TYPE stays wide enough to hold one, same reasoning as
   RETIRED_PORTFOLIO_KINDS in ./portfolio. */
export const RETIRED_DIRECTORY_SECTIONS = ["prototype"] as const;
export type DirectorySection =
  | (typeof DIRECTORY_SECTIONS)[number]
  | (typeof RETIRED_DIRECTORY_SECTIONS)[number];

/* typeof-guarded membership check for a plain string tuple */
const isOneOf = <T extends string>(list: readonly T[], value: unknown): value is T =>
  typeof value === "string" && (list as readonly string[]).includes(value);

export interface DirectoryInput {
  section: DirectorySection;
  name: string;
  /* null = the section's root */
  parentId: string | null;
}

export interface Directory extends DirectoryInput {
  id: string;
  /* effective-dating stamp (settled decision 4), attributed (AA4) */
  recorded: Stamp;
}

/* Every branch is a 400 reason, never a thrown 500 (same rule as
   validateProjectInput). `existing` is the section-spanning list the store
   already holds — parent existence and sibling duplicates are judged
   against it. */
export function validateDirectoryInput(input: unknown, existing: Directory[]): string | null {
  if (typeof input !== "object" || input === null || Array.isArray(input))
    return "a directory object is required";
  const { section, name, parentId } = input as Partial<DirectoryInput>;
  if (!isOneOf(DIRECTORY_SECTIONS, section))
    return "section must be projects, product, or platform";
  if (typeof name !== "string" || !name.trim()) return "a folder name is required";
  if (parentId !== null && typeof parentId !== "string") return "parentId must be an id or null";
  const parent = parentId === null ? null : existing.find((d) => d.id === parentId);
  if (parentId !== null && !parent) return "the parent folder does not exist";
  if (parent && parent.section !== section) return "the parent folder is in another section";
  /* two "Mining" folders side by side is a filing error, not a feature (DIR4) */
  const twin = existing.find(
    (d) =>
      d.section === section &&
      d.parentId === (parentId ?? null) &&
      d.name.trim().toLowerCase() === name.trim().toLowerCase(),
  );
  if (twin) return `a folder named "${name.trim()}" already exists here`;
  return null;
}

/* A record's directoryId is null (honestly Not filed, DIR3) or a real folder
   of the record's own section. */
export function validateDirectoryId(
  directoryId: unknown,
  section: DirectorySection,
  existing: Directory[],
): string | null {
  if (directoryId === null || directoryId === undefined) return null;
  if (typeof directoryId !== "string") return "directoryId must be an id or null";
  const dir = existing.find((d) => d.id === directoryId);
  if (!dir) return "the directory does not exist";
  if (dir.section !== section) return "the directory is in another section";
  return null;
}

/* Every folder at or below `id` — the set a rail selection covers (DIR12).

   A parent folder is a CATEGORY, not one more filing slot beside its
   children: "Platform" means everything under Platform. Counting and
   filtering only the DIRECTLY filed records made a collapsed "Platform 0"
   sit above a child holding a project, and clicking it opened an empty
   table that read as "nothing here" (Lewis, Sep 2 2026). Because a tree
   gives every record exactly one parent, scoping this way also restores the
   invariant the shallow count broke: the top-level folders plus Not filed
   partition the section, so their counts sum to All.

   The unknown id returns just itself rather than everything — the same rule
   directoryPath follows, and the honest answer for a deleted folder is an
   empty result, never the whole section. The visited set is insurance: a
   parentId cycle cannot be created through validateDirectoryInput, but a
   hand-edited or migrated row must not spin the rail forever. */
export function subtreeIds(id: string, all: readonly Directory[]): Set<string> {
  const scope = new Set<string>([id]);
  let frontier = [id];
  while (frontier.length) {
    const next: string[] = [];
    for (const d of all) {
      if (d.parentId !== null && frontier.includes(d.parentId) && !scope.has(d.id)) {
        scope.add(d.id);
        next.push(d.id);
      }
    }
    frontier = next;
  }
  return scope;
}

/* "Mining / Haul Trucks" — the select's option label and the tree's sort key
   (DIR5). A dangling parentId renders what it honestly can rather than
   throwing: the child's own name still names it. */
export function directoryPath(id: string, all: Directory[]): string {
  const parts: string[] = [];
  let cur = all.find((d) => d.id === id);
  while (cur) {
    parts.unshift(cur.name);
    cur = cur.parentId === null ? undefined : all.find((d) => d.id === cur!.parentId);
  }
  return parts.join(" / ");
}
